import { useEffect, useState } from 'react';
import {
  getCharacter,
  listCharacters,
  listClaims,
  type CharacterRow,
  type ClaimRow,
} from '../api';
import { useAsync } from '../lib/async';
import { FamilyFilter, NO_FAMILY, byFamily, serverFamily } from '../lib/family';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import { replace } from '../lib/route';
import {
  Empty,
  Facts,
  Id,
  Link,
  LocalFilterNote,
  Pill,
  Result,
  StatusPill,
  Toolbar,
  money,
  when,
} from '../lib/ui';

/**
 * Every character, and the claim rows that pretend to be characters.
 *
 * 30 of the 63 rows in `showling-characters` are `idem#…` idempotency claims
 * with no name, no portrait and no family. They are hidden by default — a
 * browser in which half the rows are garbage is not a browser — and one button
 * away, because the reason they exist is a real defect an operator should be
 * able to see: the handler writes a 24-hour `ttl` and the table has TTL
 * DISABLED, so nothing sweeps them and they are permanent.
 *
 * No portrait is rendered. A character belongs to a household, so its portrait
 * and its rejected candidates are that family's art, and the API returns S3 keys
 * rather than signed URLs for exactly that reason. Showing the key is the
 * honest amount: enough for an operator who genuinely needs the file, not a
 * gallery of children's characters on a staff screen.
 */
export function Characters({ initialFamily }: { initialFamily?: string }) {
  const [family, setFamily] = useState(initialFamily ?? '');
  const [claims, setClaims] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    replace({ name: 'characters', ...(family ? { familyId: family } : {}) });
  }, [family]);

  return (
    <>
      <Toolbar>
        <FamilyFilter value={family} onChange={setFamily} />
        <span className="spacer" />
        <button className={claims ? 'on' : ''} onClick={() => setClaims(!claims)}>
          {claims ? 'showing claim rows' : 'show the claim rows'}
        </button>
      </Toolbar>

      {claims ? (
        <Claims />
      ) : (
        <Rows family={family} open={open} onOpen={setOpen} />
      )}
    </>
  );
}

function Rows({
  family,
  open,
  onOpen,
}: {
  family: string;
  open: string | null;
  onOpen: (id: string | null) => void;
}) {
  const familyParam = serverFamily(family);
  const list = useCursorList<CharacterRow>(
    (cursor) =>
      listCharacters({
        ...(familyParam ? { family: familyParam } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [familyParam ?? ''],
  );

  return (
    <Result of={list.result} what="characters" retry={list.reload}>
      {(page) => {
        const rows = family === NO_FAMILY ? byFamily(page.rows, family) : page.rows;
        return (
          <>
            <Toolbar>
              <Pager list={list} what="characters" shown={rows.length} />
            </Toolbar>
            <LocalFilterNote
              active={family === NO_FAMILY}
              what="characters"
              why="No endpoint has a query for 'rows with no familyId'."
            />
            {!rows.length ? (
              <Empty what="characters" filtered={Boolean(family)} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Family</th>
                    <th>Status</th>
                    <th>Scope</th>
                    <th>Art</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.characterId}>
                      <td>
                        {row.characterName || <span className="muted">unnamed</span>}
                        {row.starterSlug && (
                          <Pill title="created from a starter rather than described from scratch">
                            {row.starterSlug}
                          </Pill>
                        )}
                        <div className="muted small clamp" title={row.canonical}>
                          {row.canonical}
                        </div>
                      </td>
                      <td>
                        {row.familyId ? (
                          <Link to={{ name: 'families', familyId: row.familyId }}>
                            {row.familyName || <Id value={row.familyId} />}
                          </Link>
                        ) : (
                          <span className="muted">shared</span>
                        )}
                      </td>
                      <td>
                        <StatusPill status={row.status} />
                      </td>
                      <td className="muted small">{row.reuse}</td>
                      <td className="muted small nowrap">
                        {money(row.artCostUsd)}
                        {row.candidateCount ? ` · ${row.candidateCount} candidates` : ''}
                      </td>
                      <td className="muted nowrap">{when(row.createdAt)}</td>
                      <td>
                        <button
                          className="link"
                          onClick={() => onOpen(open === row.characterId ? null : row.characterId)}
                        >
                          {open === row.characterId ? 'close' : 'open'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ScanNote listing={page} />
            {open && <Detail characterId={open} />}
          </>
        );
      }}
    </Result>
  );
}

function Detail({ characterId }: { characterId: string }) {
  const { result, reload } = useAsync(() => getCharacter(characterId), [characterId]);
  return (
    <Result of={result} what="this character" retry={reload}>
      {(character) => (
        <section className="detail">
          <h2>{character.characterName || 'unnamed'}</h2>
          <Facts
            rows={[
              ['Id', <Id value={character.characterId} chars={40} />],
              ['Canonical', <code className="small">{character.canonical}</code>],
              ['Description', character.description],
              ['Scope', character.reuse],
              ['Portrait key', <code className="small">{character.portraitKey}</code>],
              ['Portrait tier', character.portraitTier],
              ['Art cost', money(character.artCostUsd)],
              [
                'Candidates',
                character.candidates?.length
                  ? `${character.candidates.length} offered, #${character.chosenCandidate ?? '?'} chosen`
                  : null,
              ],
              ['Selected', when(character.selectedAt)],
              ['Canon version', character.canonVersion],
            ]}
          />
          {character.mediaNote && (
            <p className="muted small">
              {character.mediaNote}. An operator who genuinely needs to see the
              image has the key and the container; signing here would route
              around the same refusal the asset viewer makes.
            </p>
          )}
        </section>
      )}
    </Result>
  );
}

function Claims() {
  const list = useCursorList<ClaimRow>(
    (cursor) => listClaims('characters', cursor ? { next: cursor } : {}),
    ['character-claims'],
  );
  return (
    <Result of={list.result} what="claim rows" retry={list.reload}>
      {(page) => (
        <>
          <div className="notice notice-warn">
            <b>Idempotency claim rows — permanent, and roughly half this table.</b>
            <span className="muted small">
              `characters.py` writes a 24-hour `ttl` on these. TTL is DISABLED on
              showling-characters, so nothing sweeps them. "Overdue" below is how
              long ago each row asked to be deleted and was not.
            </span>
          </div>
          <Toolbar>
            <Pager list={list} what="claim rows" />
          </Toolbar>
          {!page.rows.length ? (
            <Empty what="claim rows" />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Family</th>
                  <th>Written</th>
                  <th>Asked to expire</th>
                  <th>Overdue</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={row.characterId ?? row.commissionId} className="flagged">
                    <td>
                      <Id value={row.characterId ?? row.commissionId} chars={26} />
                    </td>
                    <td>
                      <Id value={row.familyId} />
                    </td>
                    <td className="muted">{when(row.at)}</td>
                    <td className="muted">{when(row.ttlAt)}</td>
                    <td className={row.ttlExpired ? 'neg' : 'muted'}>
                      {row.ttlOverdueDays ? `${row.ttlOverdueDays} days` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <ScanNote listing={page} />
        </>
      )}
    </Result>
  );
}
